// ServicesList.js (Services Management Component)
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Select,
  Row,
  Col,
  Typography,
  Tooltip,
  Space,
  Switch,
  Popconfirm,
  Image,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Upload,
  DatePicker,
  Alert,
} from "antd";
import {
  UnorderedListOutlined,
  ReloadOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  UploadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import dayjs from "dayjs";

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

function ServicesList(props) {
  const [filters, setFilters] = useState({
    pageNumber: 1,
    service_type_id: "all",
    price_type: "all",
    is_available: "all",
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [editForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [editFileList, setEditFileList] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  // API hooks
  const [servicesResponse, getServices] = useApi();
  const [serviceTypesResponse, getServiceTypes] = useApi();
  const [updateResponse, updateService] = useApi(
    "/admin/services/update",
    null,
    API_METHODS.PUT,
    true
  );
  const [deleteResponse, deleteService] = useApi();
  const [toggleResponse, toggleService] = useApi();

  // In ServicesList.js - Only fetch when tab becomes active:
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (props.isActive && !hasLoaded) {
      fetchServices();
      fetchServiceTypes();
      setHasLoaded(true);
    }
  }, [props.isActive]);

  useEffect(() => {
    if (props.isActive && hasLoaded) {
      fetchServices();
    }
  }, [filters]);

  useEffect(() => {
    if (updateResponse.data && !updateResponse.isLoading) {
      messageApi.success("Service updated successfully!");
      setEditModalVisible(false);
      setEditFileList([]);
      setSelectedDates([]);
      editForm.resetFields();
      setEditingService(null);

      fetchServices(); // Refresh the list
      props.onServiceUpdated && props.onServiceUpdated(); // Notify parent if needed
    }
  }, [updateResponse]);

  // Fetch services and service types
  const fetchServices = () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        queryParams.append(key, value);
      }
    });
    getServices(
      null,
      API_METHODS.GET,
      `/admin/services/all?${queryParams.toString()}`
    );
  };

  const fetchServiceTypes = () => {
    getServiceTypes(null, API_METHODS.GET, "/admin/services/service-types");
  };

  useEffect(() => {
    fetchServices();
    fetchServiceTypes();
  }, [filters]);

  // Update service types when API responds
  useEffect(() => {
    if (serviceTypesResponse?.data && !serviceTypesResponse.isLoading) {
      setServiceTypes(serviceTypesResponse.data);
    }
  }, [serviceTypesResponse]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber: page,
    }));
  };

  // Handle edit service
  const handleEditService = (service) => {
    setEditingService(service);
    setEditFileList([]); // Reset file list

    // Set selected dates for service type 5
    if (service.service_type_id === 5 && service.available_dates) {
      setSelectedDates([...service.available_dates]);
    } else {
      setSelectedDates([]);
    }

    setEditModalVisible(true);

    // Populate form with service data
    setTimeout(() => {
      editForm.setFieldsValue({
        name: service.name,
        description: service.description,
        service_type_id: service.service_type_id,
        price_type: service.price_type,
        price: service.price,
        min_price: service.min_price,
        max_price: service.max_price,
        duration_minutes: service.duration_minutes,
        is_available: service.is_available,
        slot_required: !!service.slot_required,
      });
    }, 100);
  };

  // Watch for service type changes in edit form
  const watchedEditValues = Form.useWatch([], editForm);
  const isEditingServiceType5 = watchedEditValues?.service_type_id === 5;

  // Handle date selection for service type 5 in edit mode
  const handleEditDateChange = (date) => {
    if (date) {
      const dateString = date.format("YYYY-MM-DD");

      // Check if date is already selected
      if (!selectedDates.includes(dateString)) {
        setSelectedDates([...selectedDates, dateString]);
      } else {
        messageApi.warning("This date is already selected");
      }
    }
  };

  // Remove selected date in edit mode
  const removeEditDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((date) => date !== dateToRemove));
  };

  // Clear dates when service type changes away from 5
  useEffect(() => {
    if (editModalVisible && !isEditingServiceType5) {
      setSelectedDates([]);
    }
  }, [isEditingServiceType5, editModalVisible]);

  // Handle file upload for edit
  const handleEditUpload = ({ fileList: newFileList }) => {
    setEditFileList(newFileList);
  };

  // Validate dates for service type 5 in edit
  const validateEditDates = () => {
    if (isEditingServiceType5 && selectedDates.length === 0) {
      messageApi.error(
        "Please select at least one future date for special occasion services"
      );
      return false;
    }
    return true;
  };

  // Handle update service
  const handleUpdateService = () => {
    editForm.validateFields().then((values) => {
      // Validate dates for service type 5
      if (!validateEditDates()) {
        return;
      }

      // Check if image is uploaded
      if (editFileList.length === 0) {
        messageApi.error("Please upload a service image");
        return;
      }

      const formData = new FormData();

      // Append form fields
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          if (key === "is_available" || key === "slot_required") {
            formData.append(key, values[key] ? 1 : 0);
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      // Append selected dates for service type 5
      if (isEditingServiceType5 && selectedDates.length > 0) {
        selectedDates.forEach((date) => {
          formData.append("available_dates[]", date);
        });
      }

      // Append image file
      formData.append("service_image", editFileList[0].originFileObj);

      updateService(
        formData,
        API_METHODS.PUT,
        `/admin/services/update/${editingService.id}`,
        true
      );
    });
  };

  // Handle delete service
  const handleDeleteService = (serviceId) => {
    deleteService(
      null,
      API_METHODS.DELETE,
      `/admin/services/delete/${serviceId}`
    );
  };

  // Handle toggle availability
  const handleToggleAvailability = (serviceId) => {
    toggleService(null, API_METHODS.PUT, `/admin/services/toggle/${serviceId}`);
  };

  // Handle responses
  useEffect(() => {
    if (updateResponse.data && !updateResponse.isLoading) {
      messageApi.success("Service updated successfully!");
      setEditModalVisible(false);
      setEditFileList([]);
      setSelectedDates([]);
      editForm.resetFields();
      setEditingService(null);
      fetchServices();
    }
  }, [updateResponse]);

  useEffect(() => {
    if (deleteResponse.data && !deleteResponse.isLoading) {
      messageApi.success("Service deleted successfully!");
      fetchServices();
    }
  }, [deleteResponse]);

  useEffect(() => {
    if (toggleResponse.data && !toggleResponse.isLoading) {
      messageApi.success(
        `Service ${
          toggleResponse.data.is_available ? "enabled" : "disabled"
        } successfully!`
      );
      fetchServices();
    }
  }, [toggleResponse]);

  // Get price type color
  const getPriceTypeColor = (priceType) => {
    switch (priceType) {
      case "fixed":
        return "green";
      case "custom":
        return "blue";
      case "minimum":
        return "orange";
      default:
        return "default";
    }
  };

  // Format price display
  const formatPrice = (service) => {
    switch (service.price_type) {
      case "fixed":
        return `₹${service.price}`;
      case "custom":
        return service.max_price ? `Up to ₹${service.max_price}` : "Any Amount";
      case "minimum":
        return `Min ₹${service.min_price}${
          service.max_price ? ` - Max ₹${service.max_price}` : ""
        }`;
      default:
        return "Contact for Price";
    }
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    return dayjs(dateString).format("MMM DD, YYYY");
  };

  // Table columns
  const columns = [
    {
      title: "Service Details",
      key: "details",
      width: 300,
      render: (record) => (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            {record.image_url ? (
              <Image
                src={record.image_url}
                alt={record.name}
                width={40}
                height={40}
                style={{ borderRadius: "6px", marginRight: "8px" }}
                preview={false}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "8px",
                  fontSize: "16px",
                }}
              >
                {record.service_type_icon || "🕉️"}
              </div>
            )}
            <div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text strong style={{ fontSize: "14px" }}>
                  {record.name}
                </Text>
                {record.is_date_restricted && (
                  <Tag
                    color="orange"
                    style={{ marginLeft: "8px", fontSize: "10px" }}
                  >
                    Special
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {record.service_type_name}
              </div>
            </div>
          </div>
          {record.description && (
            <div style={{ color: "#666", fontSize: "12px" }}>
              {record.description.length > 80
                ? `${record.description.substring(0, 80)}...`
                : record.description}
            </div>
          )}
          {/* Show available dates for service type 5 */}
          {record.is_date_restricted &&
            record.available_dates &&
            record.available_dates.length > 0 && (
              <div style={{ marginTop: "4px" }}>
                <Text
                  style={{
                    fontSize: "11px",
                    color: "#f97316",
                    fontWeight: "500",
                  }}
                >
                  📅 Available:{" "}
                  {record.available_dates
                    .slice(0, 2)
                    .map((date) => dayjs(date).format("MMM DD"))
                    .join(", ")}
                  {record.available_dates.length > 2 &&
                    ` +${record.available_dates.length - 2} more`}
                </Text>
              </div>
            )}
        </div>
      ),
    },
    {
      title: "Price Type",
      dataIndex: "price_type",
      key: "price_type",
      width: 120,
      render: (priceType) => (
        <Tag color={getPriceTypeColor(priceType)}>
          {priceType?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Price",
      key: "price",
      width: 150,
      render: (record) => (
        <div>
          <div
            style={{ fontSize: "14px", fontWeight: "600", color: "#f97316" }}
          >
            <DollarOutlined style={{ marginRight: "4px" }} />
            {formatPrice(record)}
          </div>
        </div>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration_minutes",
      key: "duration_minutes",
      width: 100,
      render: (duration) => (
        <div style={{ textAlign: "center" }}>
          <ClockCircleOutlined
            style={{ marginRight: "4px", color: "#1890ff" }}
          />
          <span>{duration} min</span>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (record) => (
        <div style={{ textAlign: "center" }}>
          <Switch
            checked={record.is_available}
            onChange={() => handleToggleAvailability(record.id)}
            loading={toggleResponse.isLoading}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </div>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_on",
      key: "created_on",
      width: 120,
      render: (date) => (
        <div style={{ fontSize: "12px", color: "#666" }}>
          {new Date(date).toLocaleDateString("en-IN")}
        </div>
      ),
      sorter: (a, b) => new Date(a.created_on) - new Date(b.created_on),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (record) => (
        <Space size="small">
          <Tooltip title="Edit Service">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditService(record)}
              style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Service"
            description="Are you sure you want to delete this service?"
            onConfirm={() => handleDeleteService(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Service">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                loading={deleteResponse.isLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <UnorderedListOutlined
              style={{ marginRight: "8px", color: "#f97316" }}
            />
            Services Management
          </div>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchServices}>
            Refresh
          </Button>
        }
      >
        {/* Filters */}
        <div
          style={{
            marginBottom: "16px",
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "6px",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Filter by Service Type"
                value={filters.service_type_id}
                onChange={(value) =>
                  handleFilterChange("service_type_id", value)
                }
                style={{ width: "100%" }}
                loading={serviceTypesResponse.isLoading}
              >
                <Option value="all">All Service Types</Option>
                {serviceTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ marginRight: "8px" }}>
                        {type.icon || "🕉️"}
                      </span>
                      {type.name}
                      {type.id === 5 && (
                        <Tag
                          color="orange"
                          style={{ marginLeft: "4px", fontSize: "10px" }}
                        >
                          Special
                        </Tag>
                      )}
                    </div>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Filter by Price Type"
                value={filters.price_type}
                onChange={(value) => handleFilterChange("price_type", value)}
                style={{ width: "100%" }}
              >
                <Option value="all">All Price Types</Option>
                <Option value="fixed">
                  <Tag color="green" style={{ margin: 0 }}>
                    FIXED
                  </Tag>
                </Option>
                <Option value="custom">
                  <Tag color="blue" style={{ margin: 0 }}>
                    CUSTOM
                  </Tag>
                </Option>
                <Option value="minimum">
                  <Tag color="orange" style={{ margin: 0 }}>
                    MINIMUM
                  </Tag>
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Filter by Status"
                value={filters.is_available}
                onChange={(value) => handleFilterChange("is_available", value)}
                style={{ width: "100%" }}
              >
                <Option value="all">All Status</Option>
                <Option value="1">Active</Option>
                <Option value="0">Inactive</Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <FilterOutlined style={{ color: "#666" }} />
                <Text style={{ color: "#666", fontSize: "13px" }}>
                  {servicesResponse.pagination?.count || 0} total services
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* Summary Cards */}
        {servicesResponse.data && (
          <Row gutter={16} style={{ marginBottom: "16px" }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#f97316",
                    }}
                  >
                    {servicesResponse.pagination?.count || 0}
                  </div>
                  <div style={{ color: "#666", fontSize: "12px" }}>
                    Total Services
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#52c41a",
                    }}
                  >
                    {servicesResponse.data?.filter((s) => s.is_available)
                      .length || 0}
                  </div>
                  <div style={{ color: "#666", fontSize: "12px" }}>Active</div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#ff4d4f",
                    }}
                  >
                    {servicesResponse.data?.filter((s) => !s.is_available)
                      .length || 0}
                  </div>
                  <div style={{ color: "#666", fontSize: "12px" }}>
                    Inactive
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#1890ff",
                    }}
                  >
                    {serviceTypes.length || 0}
                  </div>
                  <div style={{ color: "#666", fontSize: "12px" }}>
                    Categories
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Services Table */}
        <Table
          columns={columns}
          dataSource={servicesResponse.data || []}
          loading={servicesResponse.isLoading}
          rowKey="id"
          pagination={{
            current: filters.pageNumber,
            total: servicesResponse.pagination?.count || 0,
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} services`,
            onChange: (page) => {
              handlePageChange(page);
              const table = document.querySelector(".ant-table");
              if (table) {
                table.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            },
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Edit Service Modal */}
      <Modal
        title="Edit Service"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedDates([]);
          setEditFileList([]);
        }}
        width={900}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setEditModalVisible(false);
              setSelectedDates([]);
              setEditFileList([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={updateResponse.isLoading}
            onClick={handleUpdateService}
            style={{
              background: "linear-gradient(45deg, #f97316, #dc2626)",
              border: "none",
            }}
          >
            Update Service
          </Button>,
        ]}
      >
        {/* Current Image Display */}
        {editingService?.image_url && (
          <Card
            size="small"
            title="Current Service Image"
            style={{ marginBottom: "24px", backgroundColor: "#f9f9f9" }}
          >
            <div style={{ textAlign: "center", padding: "16px" }}>
              <Image
                src={editingService.image_url}
                alt="Current service image"
                width={200}
                height={150}
                style={{
                  borderRadius: "8px",
                  objectFit: "cover",
                  border: "2px solid #e6f7ff",
                }}
                preview={{
                  mask: <div style={{ color: "white" }}>🔍 Preview</div>,
                }}
              />
              <div style={{ marginTop: "12px" }}>
                <Tag color="blue">Current Image</Tag>
                <div
                  style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}
                >
                  Download this image if you want to keep it unchanged
                </div>
              </div>
            </div>
          </Card>
        )}

        <Form form={editForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Service Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter service name" },
                ]}
              >
                <Input placeholder="Enter service name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Service Type"
                name="service_type_id"
                rules={[
                  { required: true, message: "Please select service type" },
                ]}
              >
                <Select placeholder="Select service type">
                  {serviceTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px" }}>
                          {type.icon || "🕉️"}
                        </span>
                        {type.name}
                        {type.id === 5 && (
                          <Tag color="orange" style={{ marginLeft: "8px" }}>
                            Special Occasion
                          </Tag>
                        )}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Special Occasion Date Management - Only for Service Type 5 */}
          {isEditingServiceType5 && (
            <Card
              size="small"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <CalendarOutlined
                    style={{ marginRight: "8px", color: "#f97316" }}
                  />
                  Manage Special Occasion Dates *
                </div>
              }
              style={{
                marginBottom: "24px",
                backgroundColor: "#fff7e6",
                border: "2px solid #ffa940",
              }}
            >
              <Alert
                message="Special Occasion Service"
                description="Update the available dates for this special occasion service. Users will only be able to book on these selected dates."
                type="info"
                showIcon
                style={{ marginBottom: "16px" }}
              />

              {/* Date Picker */}
              <Form.Item label="Add New Date" style={{ marginBottom: "16px" }}>
                <DatePicker
                  size="large"
                  placeholder="Click to add a date"
                  style={{ width: "100%" }}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf("day");
                  }}
                  onChange={handleEditDateChange}
                  value={null}
                />
              </Form.Item>

              {/* Selected Dates Display */}
              {selectedDates.length > 0 && (
                <div>
                  <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                    Special Dates ({selectedDates.length}):
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    {selectedDates.map((date, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => removeEditDate(date)}
                        color="orange"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <CalendarOutlined style={{ marginRight: "4px" }} />
                        {formatDateForDisplay(date)}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {selectedDates.length === 0 && (
                <Alert
                  message="No dates selected"
                  description="Please select at least one future date for this special occasion service."
                  type="warning"
                  showIcon
                />
              )}
            </Card>
          )}

          <Form.Item label="Description" name="description">
            <TextArea rows={3} placeholder="Enter service description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Price Type"
                name="price_type"
                rules={[
                  { required: true, message: "Please select price type" },
                ]}
              >
                <Select placeholder="Select price type">
                  <Option value="fixed">Fixed Price</Option>
                  <Option value="custom">Custom Amount</Option>
                  <Option value="minimum">Minimum Amount</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Duration (minutes)"
                name="duration_minutes"
                rules={[{ required: true, message: "Please enter duration" }]}
              >
                <InputNumber min={5} max={480} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Availability"
                name="is_available"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Available"
                  unCheckedChildren="Unavailable"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Time Slot"
            name="slot_required"
            valuePropName="checked"
            extra="When optional, the slot picker is hidden on the booking page and the puja runs as per temple schedule."
          >
            <Switch
              checkedChildren="Slot required"
              unCheckedChildren="Slot optional"
            />
          </Form.Item>

          {/* Dynamic Price Fields */}
          <Row gutter={16}>
            {watchedEditValues?.price_type === "fixed" && (
              <Col span={12}>
                <Form.Item label="Price (₹)" name="price">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            )}
            {watchedEditValues?.price_type === "minimum" && (
              <Col span={12}>
                <Form.Item
                  label="Minimum Price (₹)"
                  name="min_price"
                  rules={[
                    { required: true, message: "Please enter minimum price" },
                  ]}
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            )}
            {(watchedEditValues?.price_type === "minimum" ||
              watchedEditValues?.price_type === "custom") && (
              <Col span={12}>
                <Form.Item label="Maximum Price (₹)" name="max_price">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Required Image Upload */}
          <Form.Item
            label="Upload New Service Image"
            rules={[{ required: true, message: "Please upload service image" }]}
            help="⚠️ Image upload is required. Download the current image above if you want to keep it unchanged."
          >
            <Upload
              listType="picture-card"
              fileList={editFileList}
              onChange={handleEditUpload}
              beforeUpload={() => false}
              accept="image/*"
              maxCount={1}
            >
              {editFileList.length === 0 && (
                <div style={{ textAlign: "center" }}>
                  <UploadOutlined
                    style={{ fontSize: "24px", color: "#f97316" }}
                  />
                  <div
                    style={{
                      marginTop: "8px",
                      color: "#f97316",
                      fontWeight: "500",
                    }}
                  >
                    Upload Image
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    JPG, PNG, WebP
                  </div>
                </div>
              )}
            </Upload>
          </Form.Item>

          {/* Service Type Info for Edit */}
          {isEditingServiceType5 && (
            <Alert
              message="Special Occasion Service"
              description={`This service will only be available on the selected dates: ${selectedDates.length} date(s) chosen. Users will see these dates when booking.`}
              type="warning"
              showIcon
              style={{ marginTop: "16px" }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default ServicesList;
